import React, { useState, useCallback, useEffect } from 'react'
import { Button, Empty, message, Space, Tabs } from 'antd'
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import { useCompany } from '../../../../contexts/CompanyContext'
import { useConfig } from '../../../../contexts/ConfigContext'
import { useRegister } from '../../../../contexts/RegisterContext'
import { useSecureRequest } from '../../../../hooks/useSecureRequest'
import { TStorageItemRS, IMovementRS } from '../../../../models/storageModel'
import InventoryReportFilters from './InventoryReportFilters'
import CurrentStockReport, { getCurrentStockExcelColumns, getCurrentStockExcelData } from './CurrentStockReport'
import StockByCategoryReport, { categoryExcelColumns, getCategoryExcelData } from './StockByCategoryReport'
import InventoryValuationReport, { valuationExcelColumns, getValuationExcelData } from './InventoryValuationReport'
import ProductRotationReport, { rotationExcelColumns, getRotationExcelData } from './ProductRotationReport'
import StockAlertsReport, { alertsExcelColumns, getAlertsExcelData } from './StockAlertsReport'
import { downloadBase64File } from '../../../../utils/excelExport'

type TInventoryReportTab = 'stock' | 'category' | 'valuation' | 'rotation' | 'alerts'

export const InventoryReportsSection: React.FC = () => {
  const { company } = useCompany()
  const { config } = useConfig()
  const { register } = useRegister()
  const { securePost } = useSecureRequest()

  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(29, 'day').startOf('day'),
    dayjs().endOf('day')
  ])
  const [registerTypeFilter, setRegisterTypeFilter] = useState<string>('ALL')
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL')
  const [storehouseFilter, setStorehouseFilter] = useState<string>('ALL')
  const [activeTab, setActiveTab] = useState<TInventoryReportTab>('stock')

  const [items, setItems] = useState<TStorageItemRS[]>([])
  const [storehouses, setStorehouses] = useState<any[]>([])
  const [recentMovements, setRecentMovements] = useState<IMovementRS[]>([])

  const [loading, setLoading] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)

  const fetchInventoryData = useCallback(async () => {
    if (!company.current?.uid) return

    setLoading(true)
    setItems([])
    setHasLoaded(true)

    try {
      const startDate = dateRange[0].valueOf()
      const endDate = dateRange[1].valueOf()

      // 1 Sola Petición HTTP al endpoint agregador en backend
      const res = await securePost('/inventory/reports/get-storage-report-summary', {
        company: company.current.uid,
        startDate,
        endDate,
        registerTypeFilter,
        categoryFilter,
        storehouseFilter,
        module: config.module
      })

      if (res?.status === 200) {
        setStorehouses(res.storehouses || [])
        setItems(res.items || [])
        setRecentMovements(res.movements || [])
        message.success(`Se cargaron ${res.items?.length || 0} elementos de inventario`)
      } else {
        message.error(res?.message || 'No fue posible cargar el informe de inventario')
      }
    } catch (error) {
      console.error('Error fetching inventory report data:', error)
      message.error('Ocurrió un error al cargar el informe de inventario')
    } finally {
      setLoading(false)
    }
  }, [categoryFilter, company.current?.uid, config.module, dateRange, registerTypeFilter, securePost, storehouseFilter])

  useEffect(() => {
    fetchInventoryData()
  }, [fetchInventoryData])

  // Filtrado local según selecciones de usuario
  const filteredItems = items.filter(item => {
    if (registerTypeFilter !== 'ALL' && item.register_type !== registerTypeFilter) return false
    if (categoryFilter !== 'ALL' && item.category !== categoryFilter) return false
    return true
  })

  const handleExport = async () => {
    if (filteredItems.length === 0) {
      message.warning('No hay datos para exportar')
      return
    }

    try {
      const dateLabel = dayjs().format('YYYYMMDD')
      let rows: any[] = []
      let columns: any[] = []

      switch (activeTab) {
        case 'stock':
          rows = getCurrentStockExcelData(filteredItems, storehouses, register.categoryOptions)
          columns = getCurrentStockExcelColumns(storehouses)
          break
        case 'category':
          rows = getCategoryExcelData(filteredItems, register.categoryOptions)
          columns = categoryExcelColumns
          break
        case 'valuation':
          rows = getValuationExcelData(filteredItems, register.categoryOptions)
          columns = valuationExcelColumns
          break
        case 'rotation':
          rows = getRotationExcelData(filteredItems, recentMovements)
          columns = rotationExcelColumns
          break
        case 'alerts':
          rows = getAlertsExcelData(filteredItems, recentMovements, register.categoryOptions)
          columns = alertsExcelColumns
          break
      }

      // Solicitud cifrada al backend para generar Excel en servidor y retornar Base64
      const res = await securePost('/inventory/reports/export-storage-excel', {
        company: company.current?.uid,
        reportType: activeTab,
        rows,
        columns,
        fileName: `Informe_Inventario_${activeTab}_${dateLabel}.xlsx`
      })

      if (res?.status === 200 && res.fileContentBase64) {
        downloadBase64File(res.fileContentBase64, res.fileName || `Informe_Inventario_${dateLabel}.xlsx`)
        message.success('Archivo Excel cifrado descargado exitosamente desde el servidor')
      } else {
        message.error('No se pudo generar el archivo Excel en el servidor')
      }
    } catch (err) {
      console.error('Error al exportar Excel:', err)
      message.error('Error al descargar el archivo Excel cifrado')
    }
  }

  const tabItems = [
    {
      key: 'stock',
      label: 'Existencias Actuales',
      children: (
        <CurrentStockReport
          data={filteredItems}
          storehouses={storehouses}
          categoryOptions={register.categoryOptions}
          selectedStorehouseUid={storehouseFilter}
          loading={loading}
        />
      )
    },
    {
      key: 'category',
      label: 'Por Categoría',
      children: (
        <StockByCategoryReport
          data={filteredItems}
          categoryOptions={register.categoryOptions}
          loading={loading}
        />
      )
    },
    {
      key: 'valuation',
      label: 'Valoración de Inventario',
      children: (
        <InventoryValuationReport
          data={filteredItems}
          categoryOptions={register.categoryOptions}
          loading={loading}
        />
      )
    },
    {
      key: 'rotation',
      label: 'Rotación de Productos',
      children: (
        <ProductRotationReport
          items={filteredItems}
          movements={recentMovements}
          loading={loading}
        />
      )
    },
    {
      key: 'alerts',
      label: 'Alertas de Stock',
      children: (
        <StockAlertsReport
          items={filteredItems}
          movements={recentMovements}
          categoryOptions={register.categoryOptions}
          loading={loading}
        />
      )
    }
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <InventoryReportFilters
          dateRange={dateRange}
          onDateChange={setDateRange}
          registerTypeFilter={registerTypeFilter}
          onRegisterTypeChange={setRegisterTypeFilter}
          categoryFilter={categoryFilter}
          onCategoryChange={setCategoryFilter}
          storehouseFilter={storehouseFilter}
          onStorehouseChange={setStorehouseFilter}
          categoryOptions={register.categoryOptions}
          storehouses={storehouses}
          loading={loading}
        />
        <Space wrap>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={fetchInventoryData}
            loading={loading}
          >
            Actualizar Datos
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExport}
            disabled={filteredItems.length === 0 || loading}
            style={{ borderColor: '#52c41a', color: filteredItems.length > 0 ? '#52c41a' : undefined }}
          >
            Exportar Excel
          </Button>
        </Space>
      </div>

      {hasLoaded && filteredItems.length === 0 && !loading && (
        <Empty
          description="No se encontraron elementos de inventario para el filtro seleccionado"
          style={{ padding: '60px 0' }}
        />
      )}

      {filteredItems.length > 0 && (
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as TInventoryReportTab)}
          items={tabItems}
          size="large"
          type="card"
        />
      )}
    </div>
  )
}

export default InventoryReportsSection
